import CompetitorIconUtils from '../utils.js';
import { useState, useEffect, useMemo, useCallback } from 'react';

const { replaceColors, encodeSvgString } = CompetitorIconUtils;

/**
 * React hooks for {@link Mojito.Presentation.Components.CompetitorIcon}: resolving per-competitor icon URLs from static presentation config when possible, otherwise fetching SVGs, applying optional colour overrides, and emitting base64 data URLs.
 *
 * @name Hooks
 * @memberof Mojito.Presentation.Components.CompetitorIcon
 */

/**
 * Resolves one icon source per competitor in the same order as the input array.
 *
 * @function useCompetitorsIconsQuery
 *
 * @param {object} props - Hook arguments.
 * @param {Object<object>} [props.config={}] - The config MUST BE the type of CompetitorIconOptions config. Its `icons` field provides the static icon map keyed by competitor `id`/`teamId` (with optional `default` entry).
 * @param {Array<{ id: string, teamId: string, icon: { url: string, colors: string[] } }>} [props.competitors=[]] - Rows to resolve; often `event.participants` or `event.playersInfo`.
 * @param {string} [props.sportId] - Sport ID; must be listed in `config.enabledSports` for icons to resolve. Otherwise the hook returns an empty `icons` array and never fetches.
 *
 * @returns {{ icons: string[], isLoading: boolean }} Resolved icon sources and loading flag.
 * @property {string[]} icons - One URL, file path, or base64 SVG data URL per competitor (same order as `competitors`), or `[]` while loading or when no non-default icons could be resolved.
 * @property {boolean} isLoading - `true` while remote SVG fetches are in flight; `false` when using config-only resolution or after fetch completion.
 * @memberof Mojito.Presentation.Components.CompetitorIcon.Hooks
 */
const EMPTY_CONFIG = {};
const EMPTY_COMPETITORS = [];
const EMPTY_ENABLED_SPORTS = [];
const EMPTY_DISABLED_SPORTS = [];
const EMPTY_ICONS = {};

const useCompetitorsIconsQuery = ({
    config = EMPTY_CONFIG,
    competitors = EMPTY_COMPETITORS,
    sportId,
}) => {
    const { enabled } = config;
    const iconsMap = config.icons || EMPTY_ICONS;
    const defaultIcon = iconsMap.default;
    const enabledSports = config.enabledSports || EMPTY_ENABLED_SPORTS;
    const disabledSports = config.disabledSports || EMPTY_DISABLED_SPORTS;
    const iconsAllowed =
        sportId && (enabled ? !disabledSports.includes(sportId) : enabledSports.includes(sportId));

    const configCompetitorsLogos = useMemo(
        () =>
            competitors.map(competitor => {
                const identifier = competitor?.id || competitor?.teamId;
                return iconsMap[identifier] || defaultIcon;
            }),
        [competitors, iconsMap, defaultIcon]
    );

    const hasConfigIcons = useCallback(
        resolved => resolved.some(src => src !== defaultIcon),
        [defaultIcon]
    );

    const [isLoading, setIsLoading] = useState(false);
    const [competitorIcons, setCompetitorIcons] = useState([]);

    const processIcons = useCallback(({ svgIcon, colors }) => {
        return encodeSvgString(replaceColors(svgIcon, colors));
    }, []);

    const fetchCompetitorsIcons = useCallback(
        async inputCompetitors => {
            const result = await Promise.all(
                inputCompetitors.map(async competitor => {
                    if (!competitor?.icon?.url) {
                        return defaultIcon;
                    }
                    try {
                        const response = await fetch(competitor.icon.url);
                        if (!response.ok) {
                            return defaultIcon;
                        }
                        const svgIcon = await response.text();
                        const colors = competitor.icon?.colors ?? [];
                        return processIcons({ svgIcon, colors });
                    } catch {
                        return defaultIcon;
                    }
                })
            );
            return result;
        },
        [defaultIcon, processIcons]
    );

    useEffect(() => {
        if (!iconsAllowed) {
            setCompetitorIcons([]);
            return;
        }
        if (hasConfigIcons(configCompetitorsLogos)) {
            setCompetitorIcons(configCompetitorsLogos);
            return;
        }

        let active = true;
        setIsLoading(true);
        fetchCompetitorsIcons(competitors).then(result => {
            if (!active) {
                return;
            }
            setCompetitorIcons(hasConfigIcons(result) ? result : []);
            setIsLoading(false);
        });
        return () => {
            active = false;
            setIsLoading(false);
        };
    }, [competitors, configCompetitorsLogos, fetchCompetitorsIcons, hasConfigIcons, iconsAllowed]);

    return { icons: competitorIcons, isLoading };
};

export { useCompetitorsIconsQuery };
